import Axios from "../config/axios-v1-config";

/**
 * Escribir a repartidores desde la mesa de ayuda. Cuatro contratos, todos por
 * el BFF (que inyecta el operador y valida el rol; la web nunca manda tenant):
 *
 *   1. users          PATCH users/drivers/:id/phone      -> { _id, phone }
 *   2. conversations  POST  helpdesk/contacts            -> { ok, contact }
 *   3. conversations  GET   helpdesk/drivers/:id/phones  -> { ok, driver_id, driver_name, phones }
 *   4. conversations  POST  conversations/outbound       -> { conversationId, messageId }
 *
 * Cada función devuelve `{ data }` o `{ error }`, con error normalizado
 * `{ status, code, message, retryAfterSeconds, network }`. El texto para el
 * usuario sale de utils/driverMessagingErrors.js a partir de `code`.
 */

const READ_TIMEOUT_MS = 15000;
const WRITE_TIMEOUT_MS = 20000;
// El envío espera a WhatsApp: más margen que una escritura normal. Si vence,
// el resultado es desconocido (ver sendDriverMessage).
const SEND_TIMEOUT_MS = 30000;

const UUID_V4_TEMPLATE = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";

/** UUID v4 para la clave de idempotencia del envío (va en el body, no en headers). */
export function newIdempotencyKey() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
  if (typeof cryptoApi?.randomUUID === "function") return cryptoApi.randomUUID();
  const bytes = new Uint8Array(32);
  if (typeof cryptoApi?.getRandomValues === "function") {
    cryptoApi.getRandomValues(bytes);
  } else {
    bytes.forEach((_, i) => {
      bytes[i] = Math.floor(Math.random() * 256);
    });
  }
  let i = 0;
  return UUID_V4_TEMPLATE.replace(/[xy]/g, (c) => {
    const nibble = bytes[i++] & 0x0f;
    return (c === "x" ? nibble : (nibble & 0x3) | 0x8).toString(16);
  });
}

/**
 * Normaliza un error de axios al contrato `{statusCode, code, message}` del
 * backend. `network` = no hubo respuesta (sin red, CORS o timeout).
 */
export function normalizeApiError(err) {
  const response = err?.response;
  const body = response?.data && typeof response.data === "object" ? response.data : {};
  const message = Array.isArray(body.message) ? body.message.join(" ") : body.message;
  const retryAfter = Number(body.retryAfterSeconds ?? response?.headers?.["retry-after"]);
  return {
    status: response?.status ?? null,
    code: typeof body.code === "string" ? body.code : null,
    message: typeof message === "string" ? message : null,
    retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? Math.ceil(retryAfter) : null,
    network: !response,
  };
}

async function request(call) {
  try {
    const response = await call();
    return { data: response.data };
  } catch (err) {
    return { error: normalizeApiError(err) };
  }
}

/** 1. Edita el teléfono registrado del repartidor (nacional o con +). */
export function updateDriverPhone(driverId, phone) {
  return request(() =>
    Axios.patch(
      `users/drivers/${encodeURIComponent(driverId)}/phone`,
      { phone },
      { timeout: WRITE_TIMEOUT_MS },
    ),
  );
}

/** 2. Agrega un número (E.164) a la agenda del repartidor. */
export function createDriverContact({ phone, driverId, alias }) {
  const body = { phone, driver_id: driverId };
  if (alias) body.alias = alias;
  return request(() => Axios.post("helpdesk/contacts", body, { timeout: WRITE_TIMEOUT_MS }));
}

/** 3. Números del repartidor: el registrado y los de la agenda. */
export function getDriverPhones(driverId) {
  return request(() =>
    Axios.get(`helpdesk/drivers/${encodeURIComponent(driverId)}/phones`, {
      timeout: READ_TIMEOUT_MS,
    }),
  );
}

/**
 * 4. Primer mensaje a un repartidor (inicia o reutiliza la conversación).
 *
 * Sin respuesta (timeout, red) o con 504 el mensaje pudo haber salido: se
 * marca `OUTCOME_UNKNOWN` para que la UI NO invite a reintentar a ciegas. Este
 * módulo no reintenta nada; reenviar con la misma `idempotencyKey` es decisión
 * explícita del usuario.
 */
export async function sendDriverMessage({ driverId, phone, text, idempotencyKey, integrationId }) {
  const body = { driverId, phone, text, idempotencyKey };
  if (integrationId) body.integrationId = integrationId;
  const result = await request(() =>
    Axios.post("conversations/outbound", body, { timeout: SEND_TIMEOUT_MS }),
  );
  if (result.error && (result.error.network || result.error.status === 504)) {
    return { error: { ...result.error, code: result.error.code || "OUTCOME_UNKNOWN" } };
  }
  return result;
}
